import * as React from "react";

import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import {
  useCurrentWorkspace,
  useCurrentWorkspaceId,
} from "@v2/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { getStoreData } from "@v2/utils/project-utils";
import { Check, Copy } from "lucide-react";

// ─── Copy field with icon timeout ───────────────────────────────────────────

const CopyField: React.FC<{
  label: string;
  value: string;
}> = ({ label, value }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = React.useCallback(() => {
    void navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [value]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="flex w-full items-center gap-2 overflow-hidden rounded border border-border px-2 py-1.5 text-left transition-colors hover:bg-accent/50"
    >
      <div className="min-w-0 flex-1">
        <div className="text-[10px] text-muted">{label}</div>
        <div className="truncate font-mono text-xs text-default" title={value}>
          {value}
        </div>
      </div>
      <div className="flex h-5 w-5 shrink-0 items-center justify-center">
        {copied ? (
          <Check className="h-3 w-3 text-green" />
        ) : (
          <Copy className="h-3 w-3 text-subtle" />
        )}
      </div>
    </button>
  );
};

// ─── Section with header ────────────────────────────────────────────────────

const Section: React.FC<
  React.PropsWithChildren<{
    title: string;
  }>
> = ({ title, children }) => (
  <div className="space-y-1">
    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted">
      {title}
    </div>
    <div className="space-y-1">{children}</div>
  </div>
);

// ─── Main tab ───────────────────────────────────────────────────────────────

export const QuickIdsTab: React.FC = () => {
  const workspaceId = useCurrentWorkspaceId();
  const workspace = useCurrentWorkspace();
  const projectId = useCurrentProjectId();
  const { project, elementMetadata } = useCurrentProjectContext();
  const shopifyStore = getStoreData(project);
  return (
    <div className="space-y-3">
      {/* Workspace */}
      <Section title="Workspace">
        {workspaceId && <CopyField label="Workspace ID" value={workspaceId} />}
        {workspace?.name && (
          <CopyField label="Workspace Name" value={workspace.name} />
        )}
        {workspace?.domain && (
          <CopyField label="Workspace Domain" value={workspace.domain} />
        )}
        {workspaceId && (
          <CopyField
            label="ClickHouse Namespace (workspace)"
            value={`workspace:${workspaceId}`}
          />
        )}
      </Section>

      {/* Project */}
      <Section title="Project">
        {projectId && <CopyField label="Project ID" value={projectId} />}
        {project?.name && (
          <CopyField label="Project Name" value={project.name} />
        )}
        {projectId && (
          <CopyField
            label="ClickHouse Namespace (project)"
            value={`project:${projectId}`}
          />
        )}
        {project?.hostingType && (
          <CopyField label="Hosting Type" value={project.hostingType} />
        )}
      </Section>

      {/* Shopify Integration */}
      {shopifyStore && (
        <Section title="Shopify Integration">
          {shopifyStore.shopifyUrl && (
            <CopyField label="Shopify Domain" value={shopifyStore.shopifyUrl} />
          )}
          {shopifyStore.integrationId && (
            <CopyField
              label="Integration ID"
              value={shopifyStore.integrationId}
            />
          )}
          {shopifyStore.shopifyIntegrationConfigId && (
            <CopyField
              label="Shopify Integration Config ID"
              value={shopifyStore.shopifyIntegrationConfigId}
            />
          )}
          {shopifyStore.contactEmail && (
            <CopyField
              label="Store Contact Email"
              value={shopifyStore.contactEmail}
            />
          )}
        </Section>
      )}

      {/* Page Routes */}
      {elementMetadata && elementMetadata.length > 0 && (
        <Section title="Page Routes">
          {elementMetadata.map((element) => (
            <CopyField
              key={element.id}
              label={`${element.name} (${element.type})`}
              value={element.shopifyPagePath}
            />
          ))}
        </Section>
      )}

      {/* Useful IDs */}
      <Section title="Other IDs">
        {project?.ownerWorkspaceId && (
          <CopyField
            label="Owner Workspace ID"
            value={project.ownerWorkspaceId}
          />
        )}
        {workspace?.paymentProcessor && (
          <CopyField
            label="Payment Processor"
            value={workspace.paymentProcessor}
          />
        )}
      </Section>
    </div>
  );
};
